import { useState, useEffect } from "react";
import PetCard from "./PetCard";

export default function PetsLands({ playerData }) {
  const [view, setView] = useState("land");
  const [lands, setLands] = useState([]);
  const [pets, setPets] = useState([]);
  const [shards, setShards] = useState({});

  useEffect(() => {
    setLands([]); // Reset lands
    setPets([]); // Reset pets
    setShards([]); // Reset Shards

    if (playerData) {
      setLands(playerData.lands);
      setPets(playerData.pets);
      setShards(playerData.guildMemberships || {});
    }
  }, [playerData]);

  return (
    <div className="w-full text-lg bg-[var(--panel-section-bg)] bg-opacity-80 text-[var(--text-color)] rounded-2xl shadow-lg p-6 border border-[var(--border-color)] transition-transform duration-300 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--border-color)] flex flex-col">
      <div className="flex justify-center gap-1">
        <button
          className={`px-3 py-2 border border-[var(--border-color)]  rounded-md ${
            view === "pet" ? "bg-[var(--entity-bg-color)]" : "bg-[var(--panel-section-bg)]"
          }`}
          onClick={() => setView("pet")}
        >
          <span className="lg:inline hidden">Pet Info</span>
          <span className="lg:hidden">🐾 Pets</span>
        </button>
        <button
          className={`px-4 py-2 border border-[var(--border-color)]  rounded-md ${
            view === "land" ? "bg-[var(--entity-bg-color)]" : "bg-[var(--panel-section-bg)]"
          }`}
          onClick={() => setView("land")}
        >
          <span className="lg:inline hidden">Land Info</span>
          <span className="lg:hidden">🏔️ Lands</span>
        </button>

        <button
          className={`px-4 py-2 border border-[var(--border-color)]  rounded-md ${
            view === "shard" ? "bg-[var(--entity-bg-color)]" : "bg-[var(--panel-section-bg)]"
          }`}
          onClick={() => setView("shard")}
        >
          <span className="lg:inline hidden">Shard Info</span>
          <span className="lg:hidden">💎 Shards</span>
        </button>
      </div>

      {view === "land" && (
        <div className="mt-4">
          <h2 className="text-center font-bold">Owned Lands</h2>
          {lands.length > 0 ? (
            <div className="mt-2 max-h-60 overflow-y-auto border border-[var(--entity-bg-color)] rounded-lg p-2">
              <ul className="space-y-1">
                {lands.map((land) => {
                  const landNumber = land.id.split("-")[1];
                  const landImage = `https://d31ss916pli4td.cloudfront.net/nft/pixelsfarms/${landNumber}.gif`;
                  return (
                    <li
                      key={land._id}
                      className="transition-transform duration-300 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--border-color)]  flex items-center gap-4 p-2 bg-[var(--panel-sections)]  rounded-md"
                    >
                      <img
                        src={landImage}
                        alt={`Farm Land #${landNumber}`}
                        className="w-16 h-16 object-cover rounded-lg border border-[var(--border-color)]  shadow-sm"
                      />
                      <span>Farm Land #{landNumber}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="text-center text-gray-500 mt-2">No lands to display</p>
          )}
        </div>
      )}

      {view === "pet" && (
        <div className="mt-4">
          <h2 className="text-center font-bold">Owned Pets</h2>
          {pets.length > 0 ? ( // ✅ Use updated pets state
            <div className="mt-2 max-h-60 overflow-y-auto border border-[var(--entity-bg-color)] rounded-lg p-2">
              <ul className="space-y-1">
                {pets.map((pet) => {
                  const petName = pet.avatar?.name || `Pet #${pet.tokenId}`;
                  const happiness = pet.happiness || 0;
                  const petImage = `https://pixels-server.pixels.xyz/v1/pets/image/pixelspets/${pet.tokenId}`;

                  return (
                    <li
                      key={pet.tokenId}
                      className="p-2 bg-[var(--panel-sections)]  rounded-md flex items-center gap-4 transition-transform duration-300 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--border-color)] "
                    >
                      <PetCard
                        pet={{
                          id: pet.tokenId,
                          edition: pet.traits?.Edition || "Unknown Edition",
                          luck: pet.traits?.Luck || 0,
                          speed: pet.traits?.Speed || 0,
                          strength: pet.traits?.Strength || 0,
                          smarts: pet.traits?.Smarts || 0,
                          foodCurrent: pet.streaks?.food?.current || 0,
                          foodLongest: pet.streaks?.food?.longest || 0,
                          waterCurrent: pet.streaks?.water?.current || 0,
                          waterLongest: pet.streaks?.water?.longest || 0,
                        }}
                        imageUrl={petImage}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{petName}</div>
                        <div className="text-sm text-[var(--level-bars)]">#{pet.tokenId}</div>
                        <div className="w-full bg-[var(--level-bars-bg)] rounded-full h-2 mt-1">
                          <div className=" bg-[var(--level-bars)] h-2 rounded-full transition-all duration-300" style={{ width: `${happiness}%` }}></div>
                        </div>
                        <div className="text-xs text-[var(--level-bars)] mt-1">{happiness.toFixed(1)}% Happiness</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="text-center text-gray-500 mt-2">No pets to display</p>
          )}
        </div>
      )}

      {view === "shard" && (
        <div className="mt-4">
          <h2 className="text-center font-bold">Owned Shards</h2>
          {Object.keys(shards).length > 0 ? (
            <div className="mt-2 max-h-60 overflow-y-auto border border-[var(--entity-bg-color)] rounded-lg p-2">
              <ul className="space-y-1">
                {Object.entries(shards).map(([shardName, count]) => (
                  <li
                    key={shardName}
                    className="flex items-center justify-between bg-[var(--panel-sections)] p-2 rounded-md text-xs border border-[var(--entity-bg-color)]"
                  >
                    <span className="font-medium capitalize">{shardName}</span>
                    <span className="text-right">{count}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-center text-gray-500 mt-2">No shard details to display</p>
          )}
        </div>
      )}
    </div>
  );
}
